/**
 * Kleine Helfer zum Aufbau der Oberfläche. Elemente entstehen nur über die
 * DOM-API, nie über innerHTML: Texte vom Server oder vom Gerät können so nie
 * als HTML ausgeführt werden.
 */

import { STATUS_SYMBOL, STATUS_TEXT, type Status } from '../shared/pruefpunkte.js';

type Kind = Node | string | number | null | undefined | false;
type Attribute = Record<string, string | number | boolean | null | undefined | EventListener>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attribute: Attribute = {}, ...kinder: (Kind | Kind[])[]): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  for (const [name, wert] of Object.entries(attribute)) {
    if (wert === undefined || wert === null || wert === false) continue;
    if (typeof wert === 'function') {
      element.addEventListener(name.replace(/^on/, ''), wert);
    } else if (wert === true) {
      element.setAttribute(name, '');
    } else {
      element.setAttribute(name, String(wert));
    }
  }
  haengeAn(element, kinder.flat());
  return element;
}

export function haengeAn(eltern: Node, kinder: Kind[]): void {
  for (const kind of kinder) {
    if (kind === null || kind === undefined || kind === false) continue;
    eltern.appendChild(typeof kind === 'string' || typeof kind === 'number' ? document.createTextNode(String(kind)) : kind);
  }
}

export function ersetze(eltern: Element, ...kinder: Kind[]): void {
  eltern.replaceChildren();
  haengeAn(eltern, kinder);
}

/** Status immer mit Symbol und Wort, nie nur über die Farbe. */
export function statusMarke(status: Status): HTMLSpanElement {
  return h('span', { class: `marke marke-${status}` }, h('span', { 'aria-hidden': 'true' }, STATUS_SYMBOL[status]), ' ', STATUS_TEXT[status]);
}

export function knopf(text: string, beiKlick: () => unknown, art: 'primaer' | 'sekundaer' | 'gefahr' = 'primaer', attribute: Attribute = {}): HTMLButtonElement {
  const k = h('button', { type: 'button', class: `knopf knopf-${art}`, ...attribute }, text);
  k.addEventListener('click', () => {
    if (k.getAttribute('aria-busy') === 'true') return;
    const ergebnis = beiKlick();
    if (ergebnis instanceof Promise) {
      k.setAttribute('aria-busy', 'true');
      ergebnis.finally(() => k.removeAttribute('aria-busy')).catch(() => {});
    }
  });
  return k;
}

/** Hinweisbereich, der Änderungen für Screenreader ansagt. */
export function meldung(): HTMLParagraphElement {
  return h('p', { class: 'meldung', role: 'status', 'aria-live': 'polite' });
}

export function setzeMeldung(ziel: HTMLElement, text: string, art: 'info' | 'ok' | 'fehler' = 'info'): void {
  ziel.className = `meldung meldung-${art}`;
  ziel.textContent = text;
}

export function abschnitt(titel: string, ...inhalt: (Kind | Kind[])[]): HTMLElement {
  const id = `abschnitt-${titel.toLowerCase().replace(/[^a-z0-9äöüß]+/g, '-')}`;
  return h('section', { class: 'karte', 'aria-labelledby': id }, h('h2', { id }, titel), ...inhalt);
}

export function schritte(...texte: string[]): HTMLOListElement {
  return h('ol', { class: 'schritte' }, ...texte.map((t) => h('li', {}, t)));
}

export function zeile(beschriftung: string, wert: Kind): HTMLDivElement {
  return h('div', { class: 'zeile' }, h('dt', {}, beschriftung), h('dd', {}, wert));
}

export function liste(...zeilen: (HTMLDivElement | false | null | undefined)[]): HTMLDListElement {
  return h('dl', { class: 'werte' }, ...zeilen);
}
